import type { GenderPreset } from '../theme';
import type { Baby, Leap, Measurement, Milestone, MilestoneStatus } from './types';
import type {
  BabyMilestoneRow,
  BabyRow,
  GrowthMetricServer,
  GrowthRecordRow,
  LeapRow,
  ServerMilestoneStatus,
} from './dto';
import { type GrowthUnitSystem } from './growth';

/* ── Conversions ────────────────────────────────────────────────────── */

/** Average weeks per calendar month, used everywhere we go between the two. */
const WEEKS_PER_MONTH = 4.345;

/* ── Baby ───────────────────────────────────────────────────────────── */

/** Build the UI-facing Baby shape from the server row. */
export function rowToBaby(r: BabyRow): Baby {
  const dob = new Date(r.date_of_birth + 'T00:00:00Z');
  const now = new Date();
  const days = Math.max(0, Math.floor((now.getTime() - dob.getTime()) / 86_400_000));
  const ageWeeks = Math.floor(days / 7);
  const ageMonths = days / 30.4375;
  return {
    id: r.id,
    name: r.name,
    dob: formatDob(r.date_of_birth),
    dobIso: r.date_of_birth,
    initials: initialsFor(r.name),
    ageWeeks,
    ageMonths: Math.round(ageMonths * 10) / 10,
    preset: (r.gender as GenderPreset) ?? 'neutral',
    current: false,
    role: r.role,
    canUploadMedia: r.role === 'owner' || r.role === 'editor' || r.can_upload_media === 1,
  };
}

function formatDob(iso: string): string {
  try {
    const d = new Date(iso + 'T00:00:00Z');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase();
}

/* ── Leap ───────────────────────────────────────────────────────────── */

/** Server row → UI Leap. `short` is derived from the full name. */
export function rowToLeap(r: LeapRow): Leap {
  return {
    id: r.id,
    week: r.approximate_age_weeks,
    name: r.name,
    short: shortLeapName(r.name),
    desc: r.description,
    fussyDesc: r.fussy_phase_description,
    skillDesc: r.skill_phase_description,
  };
}

/**
 * Pull a one-word label out of a leap name. "The World of Senses" → "Senses".
 * Falls back to the full name if the pattern doesn't match.
 */
function shortLeapName(full: string): string {
  const m = full.match(/^The World of (.+)$/i);
  if (!m) return full;
  const tail = m[1]!.trim();
  // For multi-word tails like "Smooth Transitions" → just "Transitions"
  const words = tail.split(/\s+/);
  return words[words.length - 1] ?? tail;
}

/* ── Milestone ──────────────────────────────────────────────────────── */

/**
 * Mapping from the underscore-cased group_key in the DB to the UI's
 * (cat, group) labels. Adding a new key here is the only place to touch
 * when a new group is seeded.
 */
const GROUP_KEY_LABELS: Record<string, { cat: string; group: string }> = {
  // Existing snake_case keys (for safety)
  motor_gross:      { cat: 'Motor Skills',         group: 'Motor – Gross' },
  motor_fine:       { cat: 'Motor Skills',         group: 'Motor – Fine'  },
  communication:    { cat: 'Communication',        group: 'Communication' },
  social_emotional: { cat: 'Social & Emotional',   group: 'Social'        },
  cognitive:        { cat: 'Cognitive',            group: 'Cognitive'     },

  // DB capitalized keys
  'Motor - Gross':      { cat: 'Motor Skills',         group: 'Motor – Gross' },
  'Motor - Fine':       { cat: 'Motor Skills',         group: 'Motor – Fine'  },
  'Communication':      { cat: 'Communication',        group: 'Communication' },
  'Social & Emotional': { cat: 'Social & Emotional',   group: 'Social'        },
  'Cognitive':          { cat: 'Cognitive',            group: 'Cognitive'     },

  // New DB keys
  'Boundary Stone':     { cat: 'Boundary Stones',      group: 'Boundary Stone'},
  'Language':           { cat: 'Language',             group: 'Language'      },
  'Nutrition':          { cat: 'Nutrition',            group: 'Nutrition'     },
  'Self-Care':          { cat: 'Self-Care',            group: 'Self-Care'     },
  'Sleep':              { cat: 'Sleep',                group: 'Sleep'         },
  'Teeth':              { cat: 'Teeth',                group: 'Teeth'         },
};

function labelsForGroupKey(key: string): { cat: string; group: string } {
  return GROUP_KEY_LABELS[key] ?? { cat: key, group: key };
}

/** Backend status enum → UI status code. */
export function uiStatus(s: ServerMilestoneStatus): MilestoneStatus {
  switch (s) {
    case 'achieved':    return 'done';
    case 'in_progress': return 'now';
    case 'not_started': return 'soon';
  }
}

/** Inverse — used when sending writes to the server. */
export function serverStatus(s: MilestoneStatus): ServerMilestoneStatus {
  switch (s) {
    case 'done': return 'achieved';
    case 'now':  return 'in_progress';
    case 'soon': return 'not_started';
  }
}

/** Format a Date as ISO 'YYYY-MM-DD' in the device's local timezone. */
export function toLocalIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Today as ISO 'YYYY-MM-DD' (local). */
export function todayIso(): string {
  return toLocalIso(new Date());
}

/**
 * Map a date-chip choice to an ISO date 'YYYY-MM-DD' (local):
 *   - 'today'     → today
 *   - 'yesterday' → today − 1 day
 *   - 'thisweek'  → midpoint between Monday of this week and today (rounded);
 *                   when today is Monday this is today
 *   - 'custom'    → the explicitly picked date (falls back to today)
 */
export function isoForDateChoice(choice: string, customDate?: string): string {
  const today = new Date();
  switch (choice) {
    case 'yesterday': {
      const d = new Date(today);
      d.setDate(d.getDate() - 1);
      return toLocalIso(d);
    }
    case 'thisweek': {
      // getDay(): 0=Sun … 6=Sat. Days elapsed since this week's Monday.
      const daysSinceMonday = (today.getDay() + 6) % 7;
      const mid = new Date(today);
      mid.setDate(mid.getDate() - daysSinceMonday + Math.round(daysSinceMonday / 2));
      return toLocalIso(mid);
    }
    case 'custom':
      return customDate && customDate !== '' ? customDate : toLocalIso(today);
    case 'today':
    default:
      return toLocalIso(today);
  }
}

/**
 * Reverse of isoForDateChoice: given an existing ISO date string ('YYYY-MM-DD'),
 * determine whether it corresponds to today, yesterday, or a custom date.
 */
export function dateChoiceForIso(iso?: string | null): { choice: string; customDate: string } {
  const today = todayIso();
  if (!iso || iso.trim() === '') {
    return { choice: 'today', customDate: today };
  }
  const y = new Date();
  y.setDate(y.getDate() - 1);
  const yesterday = toLocalIso(y);

  if (iso === today) {
    return { choice: 'today', customDate: today };
  }
  if (iso === yesterday) {
    return { choice: 'yesterday', customDate: today };
  }
  return { choice: 'custom', customDate: iso };
}

/**
 * Server row → UI Milestone. The screens were originally written against the
 * fixture shape (months, string ids like "m1", done/now/soon); we preserve
 * that shape so they don't need to change.
 *
 * Two notable decisions:
 *   - `id` becomes the stringified numeric id (e.g. "3"). Screen logic that
 *     uses it as a React key or in URLs doesn't care.
 *   - The (start, end, median) months are derived from the WHO-style
 *     percentile bands the schema actually stores in weeks. We treat
 *     avg_start_weeks → end of the early window and (avg_achieved + std_dev)
 *     → end of the late window so the Gantt bar covers the realistic range.
 */
export function resolveMilestoneMedia(r: {
  user_media_id?: string | null;
  user_media_type?: 'image' | 'video' | 'audio' | null;
  user_media_mime?: string | null;
  user_media_encrypted?: boolean | null;
  media?: {
    id: string;
    media_type: string;
    mime_type: string;
    is_e2e_encrypted: boolean;
  }[];
}) {
  if (r.user_media_id) {
    return {
      id: r.user_media_id,
      kind: (r.user_media_type ?? 'image') as 'image' | 'video' | 'audio',
      mimeType: r.user_media_mime ?? 'image/jpeg',
      isEncrypted: !!r.user_media_encrypted,
      // Parent-uploaded photo: private, possibly E2EE → blob-fetch path.
      isCatalog: false,
    };
  }
  const libraryImage = r.media?.find((m) => m.media_type === 'image');
  if (libraryImage) {
    return {
      id: libraryImage.id,
      kind: 'image' as const,
      mimeType: libraryImage.mime_type,
      isEncrypted: libraryImage.is_e2e_encrypted,
      // Editorial catalog illustration: public, never encrypted → can load via
      // a plain <img src={catalogMediaUrl(...)}> with native lazy + HTTP cache.
      isCatalog: true,
    };
  }
  return null;
}

export function rowToMilestone(r: BabyMilestoneRow): Milestone {
  const { cat, group } = labelsForGroupKey(r.group_key);
  const start  = round(r.avg_start_weeks / WEEKS_PER_MONTH, 1);
  const median = round(r.median_achieved_weeks / WEEKS_PER_MONTH, 1);
  const end    = round((r.avg_achieved_weeks + r.std_dev_weeks) / WEEKS_PER_MONTH, 1);

  return {
    id: String(r.milestone_id),
    cat,
    group,
    leap: r.leap_id ?? 0,
    name: r.name,
    desc: r.description,
    start,
    end: Math.max(end, start + 0.5),
    median,
    startWeeks: r.avg_start_weeks,
    endWeeks: Math.max(r.avg_achieved_weeks + r.std_dev_weeks, r.avg_start_weeks),
    medianWeeks: r.median_achieved_weeks,
    stdDevWeeks: r.std_dev_weeks,
    earlyFlagWeeks: r.early_flag_weeks,
    status: uiStatus(r.status),
    achievedAt: r.achieved_date ? `wk ${weeksBetween(r.achieved_date)}` : undefined,
    startedAt: r.started_at,
    achievedDate: r.achieved_date,
    primaryMedia: resolveMilestoneMedia(r),
  };
}

function round(n: number, decimals: number): number {
  const f = 10 ** decimals;
  return Math.round(n * f) / f;
}

/**
 * Approximate weeks-from-DOB given an ISO date string. Without the baby DOB
 * we can't compute exactly; this returns just the count of weeks back from
 * "now", which matches what the existing UI shows in the achievedAt chip.
 */
function weeksBetween(achievedIso: string): number {
  const d = new Date(achievedIso + 'T00:00:00Z');
  // Rough: ageWeeks of the baby at achievement. Without DOB we can only show
  // a generic "wk N" relative to today; the timeline screen renders this
  // label below the bar so it's OK if it's slightly off.
  const days = Math.max(0, Math.floor((Date.now() - d.getTime()) / 86_400_000));
  return Math.max(1, Math.floor(days / 7));
}

/* ── Milestone performance (Dashboard "By category") ────────────────── */

export type MilestonePerformance = 'ahead' | 'median' | 'late' | 'at_risk';

export interface PerfResult {
  perf: MilestonePerformance;
  /** Age in weeks used for the comparison (achieved age, or current age for in-progress). */
  ageWeeks: number;
  /** Standardised distance from the median: z = (age − median) / std_dev. */
  z: number;
}

/** Fractional weeks between two ISO dates (clamped at 0). */
function weeksFloat(fromIso: string, toIso: string): number {
  const a = new Date(fromIso + 'T00:00:00Z').getTime();
  const b = new Date(toIso + 'T00:00:00Z').getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.max(0, (b - a) / (7 * 86_400_000));
}

/**
 * Classify how a baby is tracking against the population timing for a milestone.
 *
 * For an achieved milestone we compare the age (weeks from DOB) at which it was
 * reached; for an in-progress one we compare the baby's age *today* — still
 * working on it. The verdict is bucketed by the z-score against the median and
 * std-dev the catalog stores:
 *   z < −1  ahead · |z| ≤ 1  in the median band · 1 < z ≤ 2  late · z > 2  at risk
 * An achieved milestone is never "at risk" (it's done) — it degrades to "late".
 *
 * Returns null for not-started milestones (nothing to compare yet).
 */
export function classifyMilestonePerformance(
  m: BabyMilestoneRow,
  dobIso: string | undefined,
  today: string,
): PerfResult | null {
  if (!dobIso) return null;
  const median = m.median_achieved_weeks || m.avg_achieved_weeks || 0;
  const sd = m.std_dev_weeks > 0 ? m.std_dev_weeks : Math.max(1, median * 0.15);

  const ageWeeks =
    m.status === 'achieved' && m.achieved_date
      ? weeksFloat(dobIso, m.achieved_date)
      : weeksFloat(dobIso, today);

  const z = (ageWeeks - median) / sd;
  let perf: MilestonePerformance =
    z < -1 ? 'ahead' : z <= 1 ? 'median' : z <= 2 ? 'late' : 'at_risk';
  if (m.status === 'achieved' && perf === 'at_risk') perf = 'late';

  // If milestone is not started and not late/at risk, it is still Upcoming, so return null
  if (m.status === 'not_started' && perf !== 'late' && perf !== 'at_risk') {
    return null;
  }

  return { perf, ageWeeks, z };
}

/* ── Growth ─────────────────────────────────────────────────────────── */

/** UI metric key (matches the Growth screen tabs) → server enum. */
export type UiGrowthMetric = 'weight' | 'height' | 'head';

export const UI_TO_SERVER_METRIC: Record<UiGrowthMetric, GrowthMetricServer> = {
  weight: 'weight',
  height: 'height',
  head:   'head_circumference',
};

const SERVER_TO_UI_METRIC: Record<GrowthMetricServer, UiGrowthMetric> = {
  weight:             'weight',
  height:             'height',
  head_circumference: 'head',
};

/**
 * Normalise a stored value to the unit the chart + table assume: kg for
 * weight, cm for lengths. The DB lets the client store other units, so we
 * convert on the way out rather than forcing one unit at write time.
 */
function toDisplayValue(metric: GrowthMetricServer, value: number, unit: string): number {
  if (metric === 'weight') {
    switch (unit) {
      case 'g':  return value / 1000;
      case 'lb':
      case 'lbs': return value * 0.453_592_37;
      case 'oz': return value * 0.028_349_523_1;
      default:   return value; // kg
    }
  }
  // height + head_circumference are lengths
  return unit === 'in' ? value * 2.54 : value; // cm
}

/**
 * Group the server's one-row-per-metric growth records into the UI's
 * one-row-per-date Measurement shape (weight + height + head together).
 *
 * `dobIso` is needed to compute age-in-weeks at each recorded date; without
 * it the chart x-axis can't place the points, so we fall back to 0.
 */
export function rowsToMeasurements(rows: GrowthRecordRow[], dobIso?: string): Measurement[] {
  const byDate = new Map<string, Measurement>();
  for (const r of rows) {
    const date = r.recorded_at; // 'YYYY-MM-DD'
    let m = byDate.get(date);
    if (!m) {
      m = {
        id: date,
        date: formatDob(date),
        dateShort: formatShort(date),
        ageWeeks: dobIso ? weeksFrom(dobIso, date) : 0,
        weightId: null,
        weight: null,
        heightId: null,
        height: null,
        headId: null,
        head: null,
        note: null,
      };
      byDate.set(date, m);
    }
    const key = SERVER_TO_UI_METRIC[r.metric];
    m[key] = toDisplayValue(r.metric, Number(r.value), r.unit);
    if (key === 'weight') m.weightId = r.id;
    if (key === 'height') m.heightId = r.id;
    if (key === 'head') m.headId = r.id;
    if (r.notes && !m.note) m.note = r.notes;
  }
  // Sort chronologically by the ISO date key (stable regardless of dobIso).
  return Array.from(byDate.values()).sort((a, b) => a.id.localeCompare(b.id));
}

function formatShort(iso: string): string {
  try {
    return new Date(iso + 'T00:00:00Z').toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

/** Whole weeks between two ISO dates (clamped at 0). */
function weeksFrom(dobIso: string, atIso: string): number {
  const dob = new Date(dobIso + 'T00:00:00Z').getTime();
  const at  = new Date(atIso + 'T00:00:00Z').getTime();
  if (Number.isNaN(dob) || Number.isNaN(at)) return 0;
  return Math.max(0, Math.floor((at - dob) / (7 * 86_400_000)));
}

export type { GrowthUnitSystem } from './growth';
